import React from "react";
import { Heart, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/components/auth/AuthProvider";
import { useGuestSaves } from "@/hooks/use-guest-saves";
import { toast } from "sonner";

interface SaveButtonProps {
  nursingHome: {
    nh_slug: string;
    center_name: string;
    county_slug: string;
    county_name?: string;
  };
  isInLeads?: boolean;
  onTrackClick: (type: string) => void;
  className?: string;
}

export function SaveButton({ 
  nursingHome, 
  isInLeads = false, 
  onTrackClick,
  className = ""
}: SaveButtonProps) {
  const { user } = useAuth();
  const { 
    isNursingHomeSaved, 
    saveNursingHome, 
    removeSavedNursingHome,
    getSavedCount 
  } = useGuestSaves();

  const isSaved = user ? isInLeads : isNursingHomeSaved(nursingHome.nh_slug);

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    onTrackClick('save_nursing_home');
    
    console.log("SaveButton clicked for:", nursingHome.center_name, "Current saved state:", isSaved);
    
    if (!user) {
      // Guest save functionality
      if (isSaved) {
        console.log("Removing from saves:", nursingHome.nh_slug);
        removeSavedNursingHome(nursingHome.nh_slug);
        toast.success("Removed from saved list", {
          description: `${nursingHome.center_name} has been removed from your saved nursing homes.`
        });
      } else {
        console.log("Adding to saves:", nursingHome);
        saveNursingHome(nursingHome);
        
        // Show immediate feedback without waiting for count
        toast.success("Saved for later!", {
          description: `${nursingHome.center_name} added to your saved list. Create an account to sync across devices.`,
          action: {
            label: "Create Account",
            onClick: () => window.location.href = "/auth/signup"
          }
        });
      }
    } else {
      // For authenticated users, this will be handled by the parent component
      // through the existing waitlist/leads functionality
      toast.info("Use the waitlist button below to save this nursing home to your account");
    }
  };

  const tooltipText = () => {
    if (user) {
      return isInLeads ? "Already in your leads" : "Use waitlist button to save";
    }
    return isSaved ? "Remove from saved" : "Save for later";
  };

  const iconColor = isSaved ? "text-red-500" : "text-gray-400 hover:text-red-500";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSaveClick}
            className={`absolute top-2 right-2 bg-white/80 backdrop-blur-sm border border-gray-200 hover:bg-white hover:scale-110 transition-all duration-200 ${className}`}
            aria-label={isSaved ? "Remove from saved" : "Save nursing home"}
          >
            {isSaved ? (
              <Heart className={`h-4 w-4 ${iconColor} fill-current`} />
            ) : (
              <Heart className={`h-4 w-4 ${iconColor}`} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}